import {
    Collapse,
    createStyles,
    Theme, Typography,
    withStyles,
    WithStyles,
} from '@material-ui/core'
import moment from 'moment'
import * as R from 'ramda'
import React from 'react'

import { SupportiveService } from "../../../../services/Model/Outcome";
import { Nullable } from "../../../../services/utils";
import OutcomeDateItem from "../../../FormTemplates/CustomOutcomeDate/OutcomeDateItem";
import OutcomeDropDownItem from "../../../FormTemplates/CustomOutcomeDropDown/OutcomeDropDownItem";
import SupportedServicesCheckboxGroup from "./SupportedServicesCheckboxGroup";

interface SupportiveServicesSectionProps extends WithStyles<typeof styles> {
    startDate: Nullable<Date>,
    endDate: Nullable<Date>,
    isServiceProvided: Nullable<boolean>,
    supportiveServices: Nullable<SupportiveService[]>
    updateServices: (serviceName: SupportiveService[]) => void
    handleServiceStartDateChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
    handleServiceEndDateChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
    handleServiceProvidedChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
}

const SupportiveServicesSection: React.FC<SupportiveServicesSectionProps> = (props: SupportiveServicesSectionProps) => {
    const {
        classes,
        endDate,
        isServiceProvided,
        startDate,
        supportiveServices,
        updateServices,
        handleServiceEndDateChange,
        handleServiceStartDateChange,
        handleServiceProvidedChange
    } = props

    const [serviceProvidedYN, setServicesProvided] = React.useState(isServiceProvided);

    React.useEffect(() => {
        setServicesProvided(isServiceProvided);
    }, [isServiceProvided]);

    return (
        <div className={classes.root} data-auto="supported-services-wrapper">
            <Typography variant="h5" className={classes.sectionSubtitle} data-auto="supported-services-label">
                Supportive Services
            </Typography>
            <Typography variant="subheading" className={classes.sectionNote} data-auto="supported-services-note">
                Please only list supportive services provided directly by your partnership.
                Do not list supportive services provided by RIOPC/RIReconnect Navigators,
                as those are reported to us separately. Please reach out to your grant advisor with any questions.
            </Typography>

            <OutcomeDropDownItem
                label="Supportive Services Provided"
                prop="serviceProvided"
                value={R.isNil(serviceProvidedYN) ? "" : serviceProvidedYN ? 'Yes' : 'No'}
                onInputChange={handleServiceProvidedChange}
                items={['Yes', 'No']}
                labelStyle={{'padding': '4px'}}
                dropDownStyle={{'padding': '4px 0'}}
                hideUnknown={true}
                required={true}
            />

            <Collapse
                in={!!serviceProvidedYN}
                style={{ transformOrigin: '0 0 0' }} {...(serviceProvidedYN ? { timeout: 750 } : {})}
            >
                <OutcomeDateItem
                    label="Start Date of Supportive Services"
                    prop="serviceStartDate"
                    value={startDate ? moment(moment(startDate).format('YYYY-MM-DD')).toDate() : ''}
                    onDateChange={handleServiceStartDateChange}
                    minDate={
                        moment('2010-01-01').format('YYYY-MM-DD')
                    }
                    required={!!serviceProvidedYN}
                    containerStyle={{ margin: '0px' }}
                />

                <OutcomeDateItem
                    label="End Date of Supportive Services"
                    prop="serviceEndDate"
                    value={endDate ? moment(moment(endDate).format('YYYY-MM-DD')).toDate() : ''}
                    onDateChange={handleServiceEndDateChange}
                    minDate={
                        startDate ? moment(startDate).add(1, 'days').format('YYYY-MM-DD') : ''
                    }
                    containerStyle={{ margin: '0px' }}
                />

                <div className={classes.section}>
                    <SupportedServicesCheckboxGroup
                        services={supportiveServices}
                        required={!!serviceProvidedYN}
                        onServiceCheck={updateServices}
                    />
                </div>
            </Collapse>
        </div>
    )
}

const styles = (theme: Theme) =>
    createStyles({
        root: {},
        section: {
          padding: '15px, 0'
        },
        sectionSubtitle: {
            padding: `${theme.spacing.unit}px 0`,
        },
        sectionNote: {
            padding: `${theme.spacing.unit}px ${theme.spacing.unit*5}px`,
        },
    })

export default withStyles(styles)(SupportiveServicesSection)
